import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { LogOut, Copy, Eye, Edit3, CheckCircle2, AlertCircle, Clock, Hourglass } from "lucide-react";
import { portalApi, portalToken } from "./api";
import { BRAND } from "@/data/brand";
import PortalWizard from "./PortalWizard";

const LICENSE_BADGE = {
  verified: { label: "License Verified", icon: CheckCircle2, color: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" },
  pending: { label: "Pending Verification", icon: Clock, color: "text-yellow-300 bg-yellow-300/10 border-yellow-300/30" },
  invalid: { label: "License Could Not Be Verified", icon: AlertCircle, color: "text-red-400 bg-red-400/10 border-red-400/30" },
  not_submitted: { label: "License Not Submitted", icon: AlertCircle, color: "text-white/50 bg-white/5 border-white/10" },
};

export default function PortalDashboard() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const editMode = location.pathname === "/digital-card/edit";

  const refresh = async () => {
    try {
      const { user: u } = await portalApi.me();
      setUser(u);
    } catch { /* interceptor will redirect */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "super_admin") nav("/digital-card/admin");
  }, [user, nav]);

  const logout = () => { portalToken.clear(); nav("/digital-card/login"); };
  const publicUrl = user?.slug ? `${window.location.origin}/card/${user.slug}` : null;

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    toast.success("Link copied");
  };

  if (loading) {
    return <main className="min-h-screen bg-midnight-deeper flex items-center justify-center text-ag-text-muted font-mono text-xs tracking-[0.3em]">LOADING…</main>;
  }
  if (!user) return null;

  // First-time setup, incomplete, or explicit edit mode
  if (!user.onboarding_complete || !user.published || editMode) {
    return <PortalWizard user={user} onUpdate={setUser} onLogout={logout} editMode={editMode && user.published} onDone={() => nav("/digital-card")} />;
  }

  const status = LICENSE_BADGE[user.license_status] || LICENSE_BADGE.not_submitted;
  const StatusIcon = status.icon;

  return (
    <main data-testid="portal-dashboard" className="min-h-screen bg-midnight-deeper px-6 py-10 sm:py-16">
      <div className="max-w-lg mx-auto">
        <header className="flex items-center justify-between mb-12">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logoWhite} alt="" className="h-9 w-auto object-contain" />
            <div className="font-mono text-[0.55rem] text-gold/80 tracking-[0.3em]">DIGITAL CARD PORTAL</div>
          </Link>
          <button onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-xs font-mono tracking-[0.22em] uppercase">
            <LogOut className="w-3.5 h-3.5" /> Log Out
          </button>
        </header>

        <div className="glass-strong p-8 sm:p-10 space-y-8">
          <div>
            <div className="font-mono text-[0.62rem] text-gold tracking-[0.32em] uppercase mb-2">My Digital Card</div>
            <h1 className="font-display-tight text-white text-3xl sm:text-4xl tracking-[-0.03em] leading-[1] font-light">
              {user.first_name} {user.last_name}
            </h1>
            <p className="mt-2 text-ag-text-secondary text-sm">{user.professional_title}</p>
          </div>

          <div className={`inline-flex items-center gap-2 px-3 py-2 border ${status.color}`} data-testid="portal-license-status">
            <StatusIcon className="w-3.5 h-3.5" />
            <span className="font-mono text-[0.62rem] tracking-[0.22em] uppercase">{status.label}</span>
          </div>

          {user.has_pending_changes && (
            <div className="border border-yellow-300/30 bg-yellow-300/5 p-4 flex items-start gap-3" data-testid="portal-pending-banner">
              <Hourglass className="w-4 h-4 text-yellow-300 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-yellow-100/90 font-light leading-relaxed">
                <strong className="font-mono tracking-[0.22em] uppercase text-yellow-200 text-[0.6rem]">Changes Pending Review</strong>
                <div className="mt-1">Your recent edits are awaiting admin approval. The public card still shows the previously approved version until approval.</div>
                {user.pending_changes && (
                  <ul className="mt-2 space-y-1 font-mono text-[0.6rem] text-yellow-100/80">
                    {Object.keys(user.pending_changes).filter((k) => k !== "submitted_at").map((k) => (
                      <li key={k}>· {k}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {publicUrl && (
            <div className="pt-2">
              <div className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.28em] uppercase mb-2">Public Link</div>
              <div className="flex items-center gap-2">
                <input value={publicUrl} readOnly className="ag-input !py-2 flex-1 text-xs" data-testid="portal-public-url" />
                <button onClick={copyLink} className="btn-ghost-blue !py-2 !px-3 !text-[0.6rem]" data-testid="portal-copy-link">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pt-4">
            <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="portal-view-card" className="btn-gold inline-flex items-center justify-center gap-2 !py-3 !text-[0.68rem]">
              <Eye className="w-3.5 h-3.5" /> View Card
            </a>
            <button onClick={() => nav("/digital-card/edit")} data-testid="portal-edit" className="btn-ghost-blue inline-flex items-center justify-center gap-2 !py-3 !text-[0.68rem]">
              <Edit3 className="w-3.5 h-3.5" /> Edit
            </button>
          </div>

          <button onClick={() => setShowQR(!showQR)} className="w-full font-mono text-[0.62rem] text-gold tracking-[0.28em] uppercase pt-4 border-t border-white/8" data-testid="portal-qr-toggle">
            {showQR ? "Hide" : "Show"} QR Code
          </button>
          {showQR && publicUrl && (
            <div className="flex justify-center pt-2" data-testid="portal-qr">
              <div className="p-4 bg-white">
                <QRCodeSVG value={publicUrl} size={200} level="H" />
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
